export const MAX_CLIP_KEYFRAMES = 60;
export const KEYFRAME_EPSILON = 0.000001;
export const ANIMATION_TIME_LIMIT = 86400;
export const ANIMATION_PROPERTIES = [
  { property: 'x', label: 'Posicion X', min: -2, max: 3, base: 0.5, step: 0.01 },
  { property: 'y', label: 'Posicion Y', min: -2, max: 3, base: 0.5, step: 0.01 },
  { property: 'scale', label: 'Escala', min: 0.05, max: 10, base: 1, step: 0.01 },
  { property: 'rotation', label: 'Rotacion', min: -360, max: 360, base: 0, step: 1 },
  { property: 'opacity', label: 'Opacidad', min: 0, max: 1, base: 1, step: 0.01 },
];
export const ANIMATION_INTERPOLATIONS = [
  { id: 'linear', label: 'Lineal' }, { id: 'ease_in', label: 'Entrada suave' },
  { id: 'ease_out', label: 'Salida suave' }, { id: 'ease_in_out', label: 'Entrada y salida suave' },
];
export const ANIMATION_PRESETS = [
  { id: 'fade-in', label: 'Fundido de entrada' }, { id: 'fade-out', label: 'Fundido de salida' },
  { id: 'zoom-in', label: 'Zoom de entrada' }, { id: 'zoom-out', label: 'Zoom de salida' },
  { id: 'slide-left', label: 'Entrada desde izquierda' }, { id: 'slide-right', label: 'Entrada desde derecha' },
  { id: 'slide-up', label: 'Entrada desde arriba' }, { id: 'slide-down', label: 'Entrada desde abajo' },
  { id: 'pop', label: 'Pop' },
];

const bounds = Object.fromEntries(ANIMATION_PROPERTIES.map(item => [item.property, item]));
const finite = (value, fallback = 0) => Number.isFinite(value) ? value : fallback;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const speed = clip => Number.isFinite(clip.speed) && clip.speed > 0 ? clip.speed : 1;
const offset = clip => finite(clip.animation_offset);
const frames = clip => Array.isArray(clip.keyframes) ? clip.keyframes : [];
const sameTime = (left, right) => Math.abs(left - right) <= KEYFRAME_EPSILON;
const inTimeBounds = time => Number.isFinite(time) && Math.abs(time) <= ANIMATION_TIME_LIMIT;
const sortFrames = keys => [...keys].sort((left, right) => left.time - right.time || left.property.localeCompare(right.property));
const baseTransform = clip => {
  const transform = { fit: 'fit', ...clip.transform };
  for (const item of ANIMATION_PROPERTIES) transform[item.property] = clamp(finite(transform[item.property], item.base), item.min, item.max);
  return transform;
};

export function animationClock(clip, absoluteTime) {
  return offset(clip) + (finite(absoluteTime, finite(clip.start)) - finite(clip.start)) * speed(clip);
}

export function keyframeAbsoluteTime(clip, keyTime) {
  return finite(clip.start) + (keyTime - offset(clip)) / speed(clip);
}

export function clipAnimationTime(clip, absoluteTime) {
  return clamp(finite(absoluteTime, finite(clip.start)), finite(clip.start), finite(clip.start) + Math.max(0, finite(clip.duration)));
}

export function visibleKeyframes(clip, property) {
  const start = offset(clip), end = start + Math.max(0, finite(clip.duration)) * speed(clip);
  return sortFrames(frames(clip).filter(key => (!property || key.property === property)
    && bounds[key.property] && Number.isFinite(key.time) && key.time >= start - KEYFRAME_EPSILON && key.time <= end + KEYFRAME_EPSILON));
}

function ease(amount, interpolation) {
  if (interpolation === 'ease_in') return amount * amount;
  if (interpolation === 'ease_out') return 1 - (1 - amount) ** 2;
  if (interpolation === 'ease_in_out') return amount < 0.5 ? 2 * amount * amount : 1 - 2 * (1 - amount) ** 2;
  return amount;
}

export function evaluateTransform(clip, absoluteTime) {
  const transform = baseTransform(clip);
  const clock = animationClock(clip, absoluteTime);
  for (const item of ANIMATION_PROPERTIES) {
    const keys = sortFrames(frames(clip).filter(key => key.property === item.property && inTimeBounds(key.time)
      && Number.isFinite(key.value) && key.value >= item.min && key.value <= item.max));
    if (!keys.length) continue;
    if (clock <= keys[0].time) { transform[item.property] = keys[0].value; continue; }
    if (clock >= keys[keys.length - 1].time) { transform[item.property] = keys[keys.length - 1].value; continue; }
    const index = keys.findIndex(key => key.time >= clock);
    const left = keys[index - 1], right = keys[index];
    const amount = ease(clamp((clock - left.time) / (right.time - left.time), 0, 1), left.interpolation);
    transform[item.property] = left.value + (right.value - left.value) * amount;
  }
  return transform;
}

export function setKeyframe(clip, property, absoluteTime, value, interpolation = 'linear') {
  const item = bounds[property];
  if (!item || !Number.isFinite(absoluteTime) || !Number.isFinite(value)
    || !ANIMATION_INTERPOLATIONS.some(option => option.id === interpolation)) return clip;
  const time = animationClock(clip, clipAnimationTime(clip, absoluteTime));
  if (!inTimeBounds(time)) return clip;
  const matching = frames(clip).filter(key => key.property === property && sameTime(key.time, time));
  const key = { property, time: matching[0]?.time ?? time, value: clamp(value, item.min, item.max), interpolation };
  if (matching.length === 1 && matching[0].value === key.value && matching[0].interpolation === interpolation) return clip;
  const remaining = frames(clip).filter(frame => frame.property !== property || !sameTime(frame.time, time));
  if (remaining.length >= MAX_CLIP_KEYFRAMES) return clip;
  return { ...clip, keyframes: sortFrames([...remaining, key]) };
}

export function removeKeyframe(clip, property, absoluteTime) {
  if (!bounds[property] || !Number.isFinite(absoluteTime)) return clip;
  const time = animationClock(clip, clipAnimationTime(clip, absoluteTime));
  const keys = frames(clip).filter(key => key.property !== property || !sameTime(key.time, time));
  return keys.length === frames(clip).length ? clip : { ...clip, keyframes: keys };
}

export function presetAnimation(clip, preset) {
  if (!ANIMATION_PRESETS.some(option => option.id === preset) || !Number.isFinite(clip.duration) || clip.duration <= 0) return clip;
  const base = baseTransform(clip);
  const span = Math.min(0.6, clip.duration / 2);
  const end = clip.duration;
  let property, points;
  if (preset === 'fade-in') { property = 'opacity'; points = [[0, 0], [span, base.opacity]]; }
  else if (preset === 'fade-out') { property = 'opacity'; points = [[end - span, base.opacity], [end, 0]]; }
  else if (preset === 'zoom-in') { property = 'scale'; points = [[0, base.scale * 0.8], [span, base.scale]]; }
  else if (preset === 'zoom-out') { property = 'scale'; points = [[end - span, base.scale], [end, base.scale * 0.8]]; }
  else if (preset === 'pop') { property = 'scale'; points = [[0, base.scale * 0.8], [span * 0.6, base.scale * 1.1], [span, base.scale]]; }
  else {
    property = ['slide-left', 'slide-right'].includes(preset) ? 'x' : 'y';
    const direction = ['slide-left', 'slide-up'].includes(preset) ? -1 : 1;
    points = [[0, base[property] + direction * 1.2], [span, base[property]]];
  }
  const remaining = frames(clip).filter(key => key.property !== property);
  if (remaining.length + points.length > MAX_CLIP_KEYFRAMES) return clip;
  const item = bounds[property];
  const keys = points.map(([local, value], index) => ({ property,
    time: offset(clip) + local * speed(clip), value: clamp(value, item.min, item.max),
    interpolation: index === points.length - 1 ? 'linear' : preset === 'pop' && index === 1 ? 'ease_in_out' : 'ease_out' }));
  if (keys.some(key => !inTimeBounds(key.time))) return clip;
  return { ...clip, keyframes: sortFrames([...remaining, ...keys]) };
}
